// The running plan each person sets for themselves (plan v4, 2026-10-15). Pure, no DB.
//
// A schedule is a list of runs: { id, weekday 1-7, type, unit 'mi' | 'min', value }.
// It is the ONE source for Today and the Running tab. Nothing increases on its own:
// after a run the target stays the same; "Change next run" is optional (less, a +0.25 mi
// shortcut for distance targets, or type a number). A race date only PROPOSES a change.
// Book cardio rules (BLS p.333-336, 358) shape the defaults and the run-day proposals:
// cardio on upper-body or off days, never leg day.

import { weeksUntilRace, hasUpcomingRace, TENK_MILES } from './run-plan.js';

export const RUN_TYPES = [
  ['easy', 'Easy run'],
  ['runwalk', 'Run/walk'],
  ['long', 'Longer run'],
  ['speed', 'Intervals'],
  ['test', 'Mile test'],
  ['lowimpact', 'Easy cardio'],
];
const TITLE = Object.fromEntries(RUN_TYPES);
const HINT = {
  easy: 'conversational pace',
  runwalk: 'jog and walk as you need',
  long: 'easy pace, just longer',
  speed: 'warm up, then short fast pieces with walk breaks',
  test: 'warm up, then 1 mile hard for time',
  lowimpact: 'bike, elliptical, rower or incline walk',
};
export const STEP_MI = 0.25;

const round2 = (n) => Math.round(n * 100) / 100;
let seq = 0;
export function newRunId() { seq += 1; return `r${Date.now().toString(36)}${seq}`; }

// Both start at 1 mile (Kenny, 2026-10-15). Runs sit on the upper-body days and
// Saturday; the 1-mile Friday doubles as the mile test. Kenny and Cassidy train together,
// so since 2026-10-22 both start from this same plan (each saves their own copy).
export function defaultSchedule() {
  return [
    { id: 'k-wed', weekday: 3, type: 'easy', unit: 'mi', value: 1 },
    { id: 'k-fri', weekday: 5, type: 'test', unit: 'mi', value: 1 },
    { id: 'k-sat', weekday: 6, type: 'lowimpact', unit: 'min', value: 30 },
  ];
}

export function cleanSchedule(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((r) => r && r.id && r.weekday >= 1 && r.weekday <= 7 && TITLE[r.type] && (r.unit === 'mi' || r.unit === 'min') && Number(r.value) > 0)
    .map((r) => ({ id: String(r.id), weekday: Number(r.weekday), type: r.type, unit: r.unit, value: round2(Number(r.value)) }))
    .sort((a, b) => a.weekday - b.weekday);
}

export function targetText(r) { return r.unit === 'min' ? `${r.value} min` : `${r.value} mi`; }
export function runTitle(r) { return TITLE[r.type] || 'Run'; }

// The week object Today and the Running tab draw (same shape the old generator made).
export function scheduleWeek(schedule) {
  const runs = cleanSchedule(schedule).map((r) => ({
    slot: r.id, weekday: r.weekday, kind: r.type, type: r.type, title: runTitle(r),
    distanceMi: r.unit === 'mi' ? r.value : null,
    minutes: r.unit === 'min' ? r.value : null,
    unit: r.unit, value: r.value,
    detail: `${targetText(r)} · ${HINT[r.type]}`,
    isTest: r.type === 'test',
  }));
  return { steady: true, own: true, phase: 'Your plan', runs };
}

// Which run-log type a planned run maps to.
export function logTypeFor(run) {
  return { test: 'timetrial', long: 'long', speed: 'intervals', lowimpact: 'lowimpact', runwalk: 'runwalk' }[run.kind || run.type] || 'easy';
}

// Optional "Change next run". choice: 'less' | 'more' | number.
// 'more' is the +0.25 mi shortcut, distance targets only. 'less' steps down by the
// same amount (or 5 min for timed runs), never below a quarter mile or 5 minutes.
export function changeTarget(run, choice) {
  const r = { ...run };
  if (typeof choice === 'number' && choice > 0) { r.value = round2(choice); return r; }
  if (choice === 'more') {
    if (r.unit !== 'mi' || r.type === 'test') return r; // a mile test stays a mile
    r.value = round2(r.value + STEP_MI);
    return r;
  }
  if (choice === 'less') {
    r.value = r.unit === 'mi' ? Math.max(STEP_MI, round2(r.value - STEP_MI)) : Math.max(5, r.value - 5);
    return r;
  }
  return r;
}

// A race ahead PROPOSES this week's longest run from the build-up table; it never
// writes to the saved plan. Returns null when there is no race ahead or nothing to change.
const LONG_MI = [6.2, 4.0, 5.5, 5.0, 4.25, 3.5];
export function raceProposal(raceDate, schedule, today = new Date()) {
  if (!hasUpcomingRace(raceDate, today)) return null;
  const weeksOut = weeksUntilRace(raceDate, today);
  const suggested = weeksOut === 0 ? Math.round(TENK_MILES * 10) / 10 : LONG_MI[Math.min(weeksOut, LONG_MI.length - 1)];
  const dist = cleanSchedule(schedule).filter((r) => r.unit === 'mi' && r.type !== 'test' && r.type !== 'speed');
  if (!dist.length) return { weeksOut, suggestedMi: suggested, runId: null };
  const longest = dist.reduce((a, b) => (b.value > a.value ? b : a));
  if (Math.abs(longest.value - suggested) < 0.01) return null;
  return { weeksOut, suggestedMi: suggested, runId: longest.id, currentMi: longest.value };
}

// Plan v4 item 2: when lifting days change, propose moving runs off leg days
// (BLS p.358). liftDays: [{ weekday, isLegs }]. Returns [{ id, from, to }] moves only;
// the caller shows them and applies them once after review.
export function proposeRunMoves(schedule, liftDays) {
  const legs = new Set(liftDays.filter((d) => d.isLegs).map((d) => d.weekday));
  const runs = cleanSchedule(schedule);
  const taken = new Set(runs.filter((r) => !legs.has(r.weekday)).map((r) => r.weekday));
  const moves = [];
  for (const r of runs) {
    if (!legs.has(r.weekday)) continue;
    // nearest day (later first) that is not a leg day and has no run yet
    let to = null;
    for (let k = 1; k <= 6 && to == null; k++) {
      for (const cand of [((r.weekday - 1 + k) % 7) + 1, ((r.weekday - 1 - k + 7) % 7) + 1]) {
        if (!legs.has(cand) && !taken.has(cand)) { to = cand; break; }
      }
    }
    if (to != null) { moves.push({ id: r.id, from: r.weekday, to }); taken.add(to); }
  }
  return moves;
}

export function applyRunMoves(schedule, moves) {
  const byId = Object.fromEntries(moves.map((m) => [m.id, m.to]));
  return cleanSchedule(schedule.map((r) => (byId[r.id] ? { ...r, weekday: byId[r.id] } : r)));
}
